import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Container,
    Alert,
    CircularProgress,
} from '@mui/material';
import dayjs from "dayjs";
import { audienceToken, resendAudienceVerificationEmail } from '@virtual-cinema/client-lib';

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showVerificationAlert, setShowVerificationAlert] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isResendLoading, setIsResendLoading] = useState(false);
    const redirectUrl = '/';

    const validateForm = () => {
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Valid email is required.');
            return false;
        }
        if (!password.trim()) {
            setError('Password is required.');
            return false;
        }
        setError('');
        return true;
    };

    const handleResendVerification = async () => {
        if (isResendLoading) return;

        setIsResendLoading(true);
        try {
            const { error } = await resendAudienceVerificationEmail(email);
            if (error) {
                setResendSuccess(false);
                setError(error.message || 'Failed to resend verification email');
                return;
            }
            setResendSuccess(true);
        } catch (err) {
            setResendSuccess(false);
            setError('Failed to resend verification email. Please try again later.');
        } finally {
            setIsResendLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (isLoading) return;
        
        setError('');
        setShowVerificationAlert(false);
        setResendSuccess(false);

        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const { data, error } = await audienceToken(email, password);

            if (data?.email_verified === false) {
                setShowVerificationAlert(true);
                return;
            }

            if (!data?.access_token) {
                setError('Login failed: Invalid response from server.');
                return;
            }

            sessionStorage.setItem("authToken", data.access_token);
            const expirationTime = dayjs().add(data.expires_in, "seconds");
            sessionStorage.setItem("tokenExpiration", expirationTime.toISOString());
            window.location.href = redirectUrl;

        } catch (err) {
            console.error("Login error:", err);
            setError("The email or password you entered is incorrect. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const commonTextFieldSx = {
        backgroundColor: '#333',
        borderRadius: '4px',
        width: '100%',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#555',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
        },
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: '5rem',
                    padding: '2rem',
                    borderRadius: '8px',
                    backgroundColor: 'black',
                    color: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                    Welcome Back to Kalakaar
                </Typography>
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: '#999',
                        textAlign: 'center',
                        fontSize: '1.1rem',
                        lineHeight: '1.5',
                        marginBottom: '2rem',
                    }}
                >
                    Log in to discover unique indie films and support creators
                </Typography>

                {showVerificationAlert ? (
                    <Box sx={{
                        width: '100%',
                        marginBottom: '1rem',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '4px',
                        padding: '16px',
                        border: '1px solid rgba(255, 87, 34, 0.3)'
                    }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 500, marginBottom: '8px' }}>
                            Please Verify Your Email
                        </Typography>
                        <Typography sx={{ color: '#fff', marginBottom: '8px', lineHeight: 1.5 }}>
                            Your email <Typography component="span" color="primary" sx={{ fontWeight: 500 }}>{email}</Typography> needs to be verified. Please check your inbox for the verification link.
                        </Typography>
                        <Typography sx={{ color: '#999', marginTop: '12px', fontSize: '0.875rem' }}>
                            Didn't receive the email? Check your spam folder or{' '}
                            <Button
                                color="primary"
                                sx={{
                                    padding: '0 4px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={handleResendVerification}
                                disabled={isResendLoading}
                            >
                                {isResendLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={16} color="primary" sx={{ mr: 1 }} />
                                        Sending...
                                    </Box>
                                ) : (
                                    'click here to resend'
                                )}
                            </Button>
                        </Typography>
                        {resendSuccess && (
                            <Alert 
                                severity="success" 
                                sx={{ 
                                    mt: 2,
                                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                                    '& .MuiAlert-message': {
                                        color: '#fff'
                                    }
                                }}
                            >
                                Verification email has been resent. Please check your inbox.
                            </Alert>
                        )}
                    </Box>
                ) : error ? (
                    <Alert
                        severity="error"
                        sx={{
                            width: '100%',
                            marginBottom: '1rem',
                            backgroundColor: 'rgba(211, 47, 47, 0.1)'
                        }}
                    >
                        {error}
                    </Alert>
                ) : null}

                <form style={{ width: '100%' }} onSubmit={handleLogin}>
                    <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                        <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                            Email
                        </Typography>
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={commonTextFieldSx}
                        />
                    </Box>

                    <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                        <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                            Password
                        </Typography>
                        <TextField
                            type="password"
                            variant="outlined"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={commonTextFieldSx}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <a href="/forgot-password" style={{ color: 'lightblue', textDecoration: 'none' }}>
                            Forgot Password?
                        </a>
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ marginTop: '1rem', height: '42px' }}
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                Logging In...
                            </Box>
                        ) : (
                            'Log In'
                        )}
                    </Button>
                </form>

                <Typography sx={{ marginTop: '1rem', color: 'white' }}>
                    Don't have an account?{' '}
                    <a href="/signup" style={{ color: 'lightblue', textDecoration: 'none' }}>
                        Sign Up
                    </a>
                </Typography>
            </Box>
        </Container>
    );
}

export default LoginPage;